// Presentation for the site profile's Performance tab: the DQS metric rollup
// for one golden site, averaged across the trials it ran.
//
// The organising idea is that the three metric groups mean different things and
// must not look alike. Index metrics divide out the trial median, so they are
// the only figures comparable across trials and they lead. Performance metrics
// are the site's own raw figures, mixing trials of very different sizes. The
// benchmark block is the trials' own medians and totals — context about the
// studies, not about the site — so it is visually separated and never coloured
// good/bad.
//
// Every value ships with n (contributing trials) and the min–max range: a bare
// mean hides that most multi-trial sites swing widely between studies.
import { Tooltip } from "antd";

import InfoTooltip from "@/components/UI/InfoTooltip";

import {
  directionHint,
  formatMetric,
  indexConsistency,
  rangeLabel,
  spreadSentence,
  type MetricDirection,
  type MetricSummary,
} from "./PerformanceMetricFormat";

/** An index within this band of 1.0 is read as "at the trial median". */
const AT_MEDIAN_TOLERANCE = 0.05;

const NEUTRAL = { text: "text-[#2B4752]", chip: "bg-[#EEF2F5] text-[#48636F]" };
const BETTER = { text: "text-[#1B7A4B]", chip: "bg-[#E7F6EE] text-[#1B7A4B]" };
const WORSE = { text: "text-[#A65A11]", chip: "bg-[#FDF0E1] text-[#A65A11]" };

/** Whether an index of `mean` is better, worse, or level with the median. */
function indexVerdict(mean: number, direction: MetricDirection) {
  if (direction === "context" || Math.abs(mean - 1) <= AT_MEDIAN_TOLERANCE) {
    return NEUTRAL;
  }
  const above = mean > 1;
  return (direction === "higher") === above ? BETTER : WORSE;
}

export function PerformanceSection({
  title,
  tooltip,
  children,
}: {
  title: string;
  tooltip: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-[#E6EEF2] bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <span className="font-mcqueenGrotesk text-sm font-bold text-[#2B4752]">
          {title}
        </span>
        <InfoTooltip title={tooltip} iconSize={12} fill="#556C75" />
      </div>
      {children}
    </section>
  );
}

/**
 * One index metric: site value ÷ that trial's median, averaged over the site's
 * trials. 1.0× is the median, so the bar is drawn against a fixed centre at
 * 1.0 rather than against the row's own maximum.
 */
export function PerformanceIndexTile({
  metric,
  trials,
}: {
  metric: MetricSummary;
  trials: Array<Record<string, number | string | null>>;
}) {
  const tone =
    metric.mean == null ? NEUTRAL : indexVerdict(metric.mean, metric.direction);
  const range = rangeLabel(metric);
  const consistency = indexConsistency(metric, trials);
  // Bar spans 0–3×, the range that holds the overwhelming majority of indexes;
  // anything beyond simply pins to the end rather than rescaling the row.
  const fill = metric.mean == null ? 0 : Math.min(metric.mean / 3, 1) * 100;

  return (
    <div className="rounded-md border border-[#EEF2F5] p-3">
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-medium text-[#2B4752]">
          {metric.label}
        </span>
        <InfoTooltip
          title={
            <>
              {metric.metric_name} — the site&apos;s value divided by the median
              site in the same trial, averaged over {metric.n} of the trials it
              ran. 1.00× is exactly the trial median.{" "}
              {directionHint(metric.direction)}
            </>
          }
          iconSize={11}
          fill="#8FA4AD"
        />
      </div>

      <div className={`mt-1 text-xl font-semibold ${tone.text}`}>
        {formatMetric(metric.mean, "ratio")}
      </div>

      {/* Fixed scale: the tick marks 1.0× so tiles are comparable side by side */}
      <div className="relative mt-2 h-1.5 rounded bg-[#F1F5F9]">
        <div
          className={`h-full rounded ${metric.mean == null ? "" : tone.chip}`}
          style={{ width: `${fill}%` }}
        />
        <div className="absolute inset-y-[-2px] left-1/3 w-px bg-[#B9C7CE]" />
      </div>

      {/* One reading, not two raw stats: how often the site actually beat the
          trial median. The min–max sits in the tooltip for anyone who wants it. */}
      <div className="mt-2 text-[11px] text-[#48636F]">
        {range ? (
          <Tooltip title={`Per-trial index ranged ${range}.`}>
            <span className="cursor-help">{consistency}</span>
          </Tooltip>
        ) : (
          consistency
        )}
      </div>
    </div>
  );
}

/**
 * One raw performance metric. No good/bad colouring: an unindexed figure is not
 * comparable across trials of different sizes, so the number stays neutral and
 * the spread carries the caveat.
 */
export function PerformanceMetricTile({ metric }: { metric: MetricSummary }) {
  return (
    <div className="rounded-md border border-[#EEF2F5] p-3">
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-medium text-[#2B4752]">
          {metric.label}
        </span>
        <InfoTooltip
          title={
            <>
              {metric.metric_name} — mean of the site&apos;s own value across
              the {metric.n} trials that reported it. Unindexed, so trials of
              different sizes are mixed together.{" "}
              {directionHint(metric.direction)}
            </>
          }
          iconSize={11}
          fill="#8FA4AD"
        />
      </div>
      <div className="mt-1 text-xl font-semibold text-[#2B4752]">
        {formatMetric(metric.mean, metric.unit)}
      </div>
      {/* The spread the average conceals, said in words rather than "n = 10". */}
      <div className="mt-2 text-[11px] text-[#48636F]">
        {spreadSentence(metric)}
      </div>
    </div>
  );
}

/** Trial-level medians and totals — the studies' figures, not the site's. */
export function PerformanceBenchmarkRow({ metric }: { metric: MetricSummary }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[#F1F5F9] py-1.5 last:border-b-0">
      <span className="text-xs text-[#556C75]">{metric.label}</span>
      <span className="whitespace-nowrap text-xs font-semibold tabular-nums text-[#2B4752]">
        {formatMetric(metric.mean, metric.unit)}
        <span className="ml-1.5 font-normal text-[#8FA4AD]">
          {metric.n > 0 ? `(${metric.n})` : "(—)"}
        </span>
      </span>
    </div>
  );
}
